/* eslint-disable react/style-prop-object */
import React from 'react';
import {Metadata, MetadataList} from '../MetadataList/MetadataList';
import {DeploymentDetailsInfo} from '../../../types/deployment/DeploymentDetailsInfo';
import {FormattedNumber, useIntl} from 'react-intl';
import fileSize from 'filesize';

const DeploymentDetailsMetadata = ({
  deploymentDetails,
}: {
  deploymentDetails: DeploymentDetailsInfo;
}) => {
  const intl = useIntl();
  return (
    <MetadataList>
      <Metadata label="prodDeployment.targetDeviceName">
        {deploymentDetails.nodeName}
      </Metadata>
      <Metadata label="prodDeployment.targetDeviceGroupName">
        {deploymentDetails?.targetDeviceGroupName}
      </Metadata>
      <Metadata label="prodDeployment.applicationName">
        {deploymentDetails.applicationName}
      </Metadata>
      <Metadata label="prodDeployment.description">
        {deploymentDetails.deploymentDescription}
      </Metadata>
      <Metadata label="prodDeployment.model">{deploymentDetails.modelName}</Metadata>
      <Metadata label="prodDeployment.dataset">
        {deploymentDetails.dataSetDistribution?.datasetName}
      </Metadata>
      <Metadata label="prodDeployment.device">{deploymentDetails.nodeName}</Metadata>
      <Metadata label="prodDeployment.dataDrift">
        {deploymentDetails.dataDrift && (
          <ul>
            {deploymentDetails.dataDrift.skewPercent != null && (
              <li key="prodDeployment_skew">
                <span className="prod-deploy-details__label">
                  {intl.formatMessage({id: 'prodDeployment.skew'})}
                </span>
                <FormattedNumber
                  style="percent"
                  value={deploymentDetails.dataDrift.skewPercent / 100}
                />
              </li>
            )}
            {deploymentDetails.dataDrift.confidencePercent != null && (
              <li key="prodDeployment_confidence">
                <span className="prod-deploy-details__label">
                  {intl.formatMessage({id: 'prodDeployment.confidence'})}
                </span>
                <FormattedNumber
                  style="percent"
                  value={deploymentDetails.dataDrift.confidencePercent / 100}
                />
              </li>
            )}
          </ul>
        )}
      </Metadata>
      {deploymentDetails.gspUtilization && (
        <Metadata label="prodDeployment.resourceUsage">
          <ul>
            {deploymentDetails.gspUtilization?.gspUtilization != null && (
              <li key="prodDeployment_gspUtil">
                <span className="prod-deploy-details__label">
                  {intl.formatMessage({id: 'prodDeployment.gspUtil'})}
                </span>
                <FormattedNumber
                  style="percent"
                  value={deploymentDetails.gspUtilization.gspUtilization / 100}
                />
              </li>
            )}
            {deploymentDetails.gspUtilization?.memoryUsage != null && (
              <li key="prodDeployment_memUtil">
                <span className="prod-deploy-details__label">
                  {intl.formatMessage({id: 'prodDeployment.memUtil'})}
                </span>
                <span>
                  {fileSize(deploymentDetails.gspUtilization.memoryUsage, {
                    base: 10,
                  })}
                </span>
              </li>
            )}
            {deploymentDetails.gspUtilization?.memoryTotal != null && (
              <li key="prodDeployment_memTotal">
                <span className="prod-deploy-details__label">
                  {intl.formatMessage({id: 'prodDeployment.memTotal'})}
                </span>
                <span>
                  {fileSize(deploymentDetails.gspUtilization.memoryTotal, {
                    base: 10,
                  })}
                </span>
              </li>
            )}
            {deploymentDetails.gspUtilization?.gspTemperature != null && (
              <li key="prodDeployment_temp">
                <span className="prod-deploy-details__label">
                  {intl.formatMessage({id: 'prodDeployment.temp'})}
                </span>
                <FormattedNumber
                  style="unit"
                  unit="celsius"
                  value={deploymentDetails.gspUtilization.gspTemperature}
                />
              </li>
            )}
            {deploymentDetails.gspUtilization?.powerConsumption != null && (
              <li key="prodDeployment_power">
                <span className="prod-deploy-details__label">
                  {intl.formatMessage({id: 'prodDeployment.power'})}
                </span>
                <span>{`${deploymentDetails.gspUtilization?.powerConsumption}W`}</span>
              </li>
            )}
          </ul>
        </Metadata>
      )}
      {deploymentDetails.deploymentStatistics?.fps ? (
        <Metadata label="prodDeployment.appStatistics">
          <ul>
            {deploymentDetails.deploymentStatistics?.fps && (
              <li key="prodDeployment_throughput">
                <span className="prod-deploy-details__label">
                  {intl.formatMessage({id: 'testCompare.throughput'})}
                </span>
                <FormattedNumber
                  value={deploymentDetails.deploymentStatistics.fps}
                  maximumFractionDigits={1}
                />
                {' fps'}
              </li>
            )}
            {deploymentDetails.deploymentStatistics?.latency != null && (
              <li key="prodDeployment_latency">
                <span className="prod-deploy-details__label">
                  {intl.formatMessage({id: 'testCompare.latency'})}
                </span>
                <FormattedNumber
                  value={deploymentDetails.deploymentStatistics.latency}
                  maximumFractionDigits={0}
                />
                {' ms'}
              </li>
            )}
            {deploymentDetails.deploymentStatistics?.inputVideoBps !== null && (
              <li key="prodDeployment_inputVideoBps">
                <span className="prod-deploy-details__label">
                  {intl.formatMessage({id: 'testCompare.inputVideoBps'})}
                </span>
                <FormattedNumber
                  value={deploymentDetails.deploymentStatistics.inputVideoBps}
                  maximumFractionDigits={0}
                />
                {' bps'}
              </li>
            )}
            {deploymentDetails.deploymentStatistics?.inputVideoFps !== null && (
              <li key="prodDeployment_inputVideoFps">
                <span className="prod-deploy-details__label">
                  {intl.formatMessage({id: 'testCompare.inputVideoFps'})}
                </span>
                <FormattedNumber
                  value={deploymentDetails.deploymentStatistics.inputVideoFps}
                  maximumFractionDigits={1}
                />
                {' fps'}
              </li>
            )}
            {deploymentDetails.deploymentStatistics?.outputVideoBps !== null && (
              <li key="prodDeployment_outputVideoBps">
                <span className="prod-deploy-details__label">
                  {intl.formatMessage({id: 'testCompare.outputVideoBps'})}
                </span>
                <FormattedNumber
                  value={deploymentDetails.deploymentStatistics.outputVideoBps}
                  maximumFractionDigits={0}
                />
                {' bps'}
              </li>
            )}
            {deploymentDetails.deploymentStatistics?.outputVideoFps !== null && (
              <li key="prodDeployment_outputVideoFps">
                <span className="prod-deploy-details__label">
                  {intl.formatMessage({id: 'testCompare.outputVideoFps'})}
                </span>
                <FormattedNumber
                  value={deploymentDetails.deploymentStatistics.outputVideoFps}
                  maximumFractionDigits={1}
                />
                {' fps'}
              </li>
            )}
          </ul>
        </Metadata>
      ) : null}
    </MetadataList>
  );
};

export default DeploymentDetailsMetadata;
